import { useLazyQuery } from '@apollo/client'
import * as DateFns from 'date-fns'
import LoadCustomerData from 'graphql/queries/LoadCustomerData'
import { AccountType, CustomerDataResponse, ProductCategory, Query } from 'graphql/types'
import { useCallback, useLayoutEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { Dispatch } from 'redux'
import { PersistState } from 'redux-persist'
import AvailabilityCheckActions, {
    AvailabilityCheckAction,
    AvailabilityCheckField,
} from 'store/AvailabilityCheck/AvailabilityCheck.actions'
import { AvailabilityCheckState } from 'store/AvailabilityCheck/AvailabilityCheck.reducer'
import BankDetailsActions, { BankDetailsAction } from 'store/BankDetails/BankDetails.actions'
import { BankDetailsState } from 'store/BankDetails/BankDetails.reducer'
import ContactDataActions, { ContactDataAction } from 'store/ContactData/ContactData.actions'
import { ContactDataState } from 'store/ContactData/ContactData.reducer'
import GeneralStateActions, { GeneralStateAction } from 'store/GeneralState/GeneralState.actions'
import { GeneralState, LoadState, ViewType, initialGeneralState } from 'store/GeneralState/GeneralState.reducer'
import PortabilityStateActions, { PortabilityStateAction } from 'store/PortabilityState/PortabilityState.actions'
import { AppState } from 'store/store'
import useURLParams from 'utils/URLParamsContex'
import { CustomerDataLoadProps } from './CustomerDataLoad'

interface CustomerDataLoadReturn {
    loadState: LoadState
    error: string
    onClickReturn: () => void
}

const useCustomerDataLoad: (props: CustomerDataLoadProps) => CustomerDataLoadReturn = (
    props: CustomerDataLoadProps,
) => {
    const { data, orderProcessType } = props
    const history = useHistory()
    const { B2B } = useURLParams()
    const dispatch =
        useDispatch<
            Dispatch<
                | AvailabilityCheckActions
                | BankDetailsActions
                | ContactDataActions
                | GeneralStateActions
                | PortabilityStateActions
            >
        >()
    const { loadState, customizeJsData, pagesList, currentPage } = useSelector((appState: AppState) => ({
        loadState: appState.generalState.loadState,
        customizeJsData: appState.generalState.customizeJsData,
        pagesList: appState.generalState.pagesList,
        currentPage: appState.generalState.currentPage,
    }))

    const availabilityDispatch = useDispatch<Dispatch<AvailabilityCheckActions>>()

    const resetState = useCallback(() => {
        dispatch({ type: AvailabilityCheckAction.SET_TO_INIT_STATE, payload: null })
        dispatch({ type: ContactDataAction.SET_TO_INIT_STATE })
        dispatch({ type: BankDetailsAction.SET_TO_INIT_STATE, payload: null })
        dispatch({ type: GeneralStateAction.SET_TO_INIT_STATE, payload: { B2B } })
        dispatch({ type: PortabilityStateAction.SET_TO_INIT_STATE, payload: null })
    }, [dispatch])

    const [errorMessage, setErrorMessage] = useState('')

    const setZip = useCallback(
        (zip: string) => {
            availabilityDispatch({ type: AvailabilityCheckAction.SET_ZIP, payload: zip })
        },
        [availabilityDispatch],
    )

    const setFocus = useCallback(
        (focusId: AvailabilityCheckField) => {
            availabilityDispatch({ type: AvailabilityCheckAction.SET_FOCUSED_FIELD, payload: focusId })
        },
        [availabilityDispatch],
    )

    const setLoadState = useCallback(
        (payload: LoadState): void => {
            dispatch({ type: GeneralStateAction.SET_LOAD_STATE, payload })
        },
        [dispatch],
    )

    const setOrderProcessType = useCallback(
        (payload: string) => {
            dispatch({ type: GeneralStateAction.SET_ORDER_PROCESS_TYPE, payload })
        },
        [dispatch],
    )

    const setAvailabilityCheck = useCallback(
        (payload: AvailabilityCheckState): void => {
            dispatch({ type: AvailabilityCheckAction.SET_AVAILABILITY_STATE, payload })
        },
        [dispatch],
    )

    const setGeneralState = useCallback(
        (payload: GeneralState): void => {
            dispatch({ type: GeneralStateAction.SET_GENERAL_STATE, payload })
        },
        [dispatch],
    )

    const updatePageList = useCallback(
        (payload: { B2B: boolean }): void => {
            dispatch({ type: GeneralStateAction.UPDATE_PAGELIST, payload })
        },
        [dispatch],
    )

    const setContactDataState = useCallback(
        (payload: ContactDataState): void => {
            dispatch({ type: ContactDataAction.SET_CONTACT_DATA_STATE_PARTIAL, payload })
        },
        [dispatch],
    )

    const setBankDetailsState = useCallback(
        (payload: BankDetailsState): void => {
            dispatch({ type: BankDetailsAction.SET_BANK_DETAILS_STATE, payload })
        },
        [dispatch],
    )

    const [loadCustomerData] = useLazyQuery<Query>(LoadCustomerData, {
        fetchPolicy: 'network-only',
        onCompleted: (data: { loadCustomerData: CustomerDataResponse }): void => {
            const CustomerData = data.loadCustomerData.data
            const productCategories = data.loadCustomerData.productCategories
            if (data.loadCustomerData.status == 'success' && CustomerData && productCategories) {
                const contactData: ContactDataState = {
                    personalOrderComment: '',
                    personalSalutation: CustomerData.Salutation,
                    personalTitle: CustomerData.Title,
                    personalName: CustomerData.FirstName,
                    personalLastName: CustomerData.LastName,
                    personalEmail: CustomerData.Email,
                    personalBirthDate: DateFns.format(
                        DateFns.parse(CustomerData.Birthdate, 'yyyy-MM-dd', new Date()),
                        'dd.MM.yyyy',
                    ),
                    personalTelephone: CustomerData.Telephone,
                    personalMobilePhone: '',
                    personalAdditionalAddressInfo: undefined,
                    useSalesPartnerEmail: false,

                    deviatingDeliveryAddress: false,
                    deliverySalutation: '',
                    deliveryTitle: '',
                    deliveryName: '',
                    deliveryLastName: '',
                    deliveryStreet: '',
                    deliveryHouseNumber: '',
                    deliveryZip: '',
                    deliveryCity: '',
                    deliveryAdditionalAddrInfo: undefined,
                    deliveryCompany: '',
                    deliveryCompanyLegalForm: '',

                    deviatingBillingAddress: false,
                    billingSalutation: '',
                    billingTitle: '',
                    billingName: '',
                    billingLastName: '',
                    billingStreet: '',
                    billingHouseNumber: '',
                    billingZip: '',
                    billingCity: '',
                    billingAdditionalAddrInfo: undefined,
                    billingCompany: '',
                    billingCompanyLegalForm: '',

                    company: CustomerData.Company,
                    companyLegalForm: CustomerData.CompanyLegalForm,
                    companyRegisterEntry: '',
                    companyLocation: '',
                    companyId: '',

                    _persist: { rehydrated: false, version: 1 } as PersistState,
                }

                if (CustomerData.BillingAddress) {
                    contactData.billingZip = CustomerData.BillingAddress.ZipCode
                    contactData.billingCity = CustomerData.BillingAddress.City
                    contactData.billingStreet = CustomerData.BillingAddress.Street
                    contactData.billingHouseNumber = CustomerData.BillingAddress.HouseNumber
                    contactData.billingSalutation = CustomerData.BillingAddress.Salutation
                    contactData.billingTitle = CustomerData.BillingAddress.Title
                    contactData.billingName = CustomerData.BillingAddress.FirstName
                    contactData.billingLastName = CustomerData.BillingAddress.LastName
                }

                const bankData: BankDetailsState = {
                    accountType: CustomerData.BankData.IsTransfer ? AccountType.TRANSFER : AccountType.IBAN,
                    consentChecked: true,
                    consentCollectMoneyFromAccountChecked: true,
                    consentSEPAChecked: true,
                    differentAccountHolder: false,
                    accountHolderData: {
                        salutation: '',
                        title: '',
                        name: '',
                        lastName: '',
                        street: '',
                        houseNumber: '',
                        zip: '',
                        city: '',
                        company: '',
                        companyLegalForm: '',
                        additionalInfoAddress: undefined,
                    },
                    iban: CustomerData.BankData.Iban,
                    transfer: CustomerData.BankData.IsTransfer,
                    isValidating: false,
                    dayOfTransfer: '15',
                    _persist: { rehydrated: false, version: 1 } as PersistState,
                }
                if (CustomerData.AccountHolderAddress) {
                    bankData.differentAccountHolder = true
                    bankData.accountHolderData = {
                        salutation: CustomerData.AccountHolderAddress.Salutation,
                        title: CustomerData.AccountHolderAddress.Title,
                        name: CustomerData.AccountHolderAddress.FirstName,
                        lastName: CustomerData.AccountHolderAddress.LastName,
                        street: CustomerData.AccountHolderAddress.Street,
                        houseNumber: CustomerData.AccountHolderAddress.HouseNumber,
                        zip: CustomerData.AccountHolderAddress.ZipCode,
                        city: CustomerData.AccountHolderAddress.City,
                        company: '',
                        companyLegalForm: '',
                        additionalInfoAddress: '',
                    }
                }

                setGeneralState({
                    ...initialGeneralState,
                    vzfID: '',
                    availableProductCategories: productCategories as ProductCategory[],
                    selectedProductCategories: [{ id: productCategories[0]?.id ?? '' }],
                    startOfDelivery: undefined,
                    startOfMarketing: undefined,
                    customizeJsData: customizeJsData,
                    currentView: ViewType.CUSTOMER_LOAD,
                    orderProcessType: orderProcessType,
                })
                setAvailabilityCheck({
                    cities: [],
                    selectedCity: CustomerData.ConnectionAddress.City,
                    focusedFieldId: AvailabilityCheckField.ZIP,
                    houseNumbers: [],
                    selectedHouseNumber: CustomerData.ConnectionAddress.HouseNumber,
                    selectedStreet: CustomerData?.ConnectionAddress.Street,
                    streets: [],
                    zip: CustomerData.ConnectionAddress.ZipCode,
                    vzf: '',
                    vzfNoResult: false,
                    _persist: { rehydrated: false, version: 1 } as PersistState,
                })
                setContactDataState(contactData)
                setBankDetailsState(bankData)
                setLoadState({
                    ...loadState,
                    loading: false,
                })
                updatePageList({ B2B })
                if (customizeJsData && customizeJsData.globalConfiguration.enableWelcomePage) {
                    history.push(pagesList[currentPage + 1].path)
                } else {
                    history.push('/Produktauswahl/' + productCategories[0]?.id)
                }
            } else {
                setErrorMessage(data.loadCustomerData.message)
                setLoadState({
                    ...loadState,
                    loading: false,
                })
            }
        },
    })

    const onClickReturn = (): void => {
        if (
            customizeJsData?.globalConfiguration.logoClickWebsite &&
            customizeJsData?.globalConfiguration.logoClickWebsite !== undefined
        )
            window.open(customizeJsData?.globalConfiguration.logoClickWebsite, '_blank')
        else {
            setZip('')
            setFocus(AvailabilityCheckField.ZIP)
            resetState()
            history.push('/')
        }
    }

    useLayoutEffect(() => {
        setLoadState({
            loading: true,
        })
        setOrderProcessType(orderProcessType)
    }, [])

    useLayoutEffect(() => {
        if (customizeJsData) {
            loadCustomerData({
                variables: {
                    data,
                },
            })
        }
    }, [customizeJsData])

    return {
        loadState,
        error: errorMessage,
        onClickReturn,
    }
}

export default useCustomerDataLoad
